import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Paper, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { useAppDispatch } from '../../../store/store';
import { approveOrDenyCPTCodeChange, fetchPendingCPTCodeChanges, fetchPlacesOfEncounter } from '../../../store/adminCPTCodesSlice.slice';
import Authorization from '../../../utils/Authorization';
import { CPTcodeCategoryEnum } from '../../../Enum/CPTcodeCategoryEnum';

const PendingCPTCodeChanges = () => {
    const dispatch = useAppDispatch();
    const { pendingCPTCodeChanges, placesOfEncounters } = useSelector((state: RootState) => state.adminCPTCodesSlice);
    const authData = new Authorization();
    const [selectedChange, setSelectedChange] = useState<any>(null);
    const [showDiffModal, setShowDiffModal] = useState(false);

    // Assume we have the permissions array from auth or roles.
    // In our simplified logic, if they can see this component, let them view.
    // The backend does hard checks anyway.

    // In C#, permissions are strings.
    const hasApprovePermission = authData?.Permissions?.includes('Approve CPT code changes') || false;
    useEffect(() => {
        dispatch(fetchPendingCPTCodeChanges());
        dispatch(fetchPlacesOfEncounter());
    }, [dispatch]);

    const handleOpenDiff = (change: any) => {
        setSelectedChange(change);
        setShowDiffModal(true);
    };

    const handleApprove = (isApproved: boolean) => {
        if (selectedChange) {
            dispatch(approveOrDenyCPTCodeChange(selectedChange.Id, isApproved));
            setShowDiffModal(false);
            setSelectedChange(null);
        }
    };

    const getPlacesNames = (ids: string[] | undefined) => {
        if (!ids || ids.length === 0) return 'None';
        return placesOfEncounters
            .filter((place) => ids.includes(place.Id))
            .map((place) => place.Name)
            .join(', ');
    };

    const getModifierNames = (codes: string[] | undefined) => {
        if (!codes || codes.length === 0) return 'None';
        return codes.join(', ');
    };

    const renderCodeDetails = (code: any, title: string, diffCode: any = null) => {
        if (!code) {
            return (
                <Paper sx={{ p: 2, flex: 1, minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="textSecondary">New CPT Code (No Original Exists)</Typography>
                </Paper>
            );
        }

        const isDiff = (field: string) => {
            if (!diffCode) return false;
            // Handle array comparison for PlaceOfEncounterIds
            if (field === 'PlaceOfEncounterIds') {
                const arr1 = code[field] || [];
                const arr2 = diffCode[field] || [];
                if (arr1.length !== arr2.length) return true;
                const sorted1 = [...arr1].sort();
                const sorted2 = [...arr2].sort();
                return !sorted1.every((val, index) => val === sorted2[index]);
            }
            return code[field] !== diffCode[field];
        };

        const getBgColor = (field: string) => (isDiff(field) ? 'rgba(255, 204, 0, 0.2)' : 'transparent');

        return (
            <Paper sx={{ p: 3, flex: 1 }}>
                <Typography variant="h6" mb={2} color="primary">
                    {title}
                </Typography>
                <Grid container spacing={2}>
                    {code.IsDeleted ? (
                        <Grid size={12}>
                            <Typography fontStyle="italic" textAlign="center" color="error.main">
                                Delete CPT Code: {code.Code}
                            </Typography>
                        </Grid>
                    ) : (
                        <>
                            <Grid size={6}>
                                <TextField
                                    label="Code"
                                    fullWidth
                                    value={code.Code || ''}
                                    InputProps={{ readOnly: true }}
                                    sx={{ backgroundColor: getBgColor('Code') }}
                                />
                            </Grid>
                            <Grid size={6}>
                                <TextField
                                    label="Category"
                                    fullWidth
                                    value={CPTcodeCategoryEnum[code.CPTcodeCategory] || ''}
                                    InputProps={{ readOnly: true }}
                                    sx={{ backgroundColor: getBgColor('CPTcodeCategory') }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <TextField
                                    label="Description"
                                    fullWidth
                                    multiline
                                    maxRows={3}
                                    value={code.Description || ''}
                                    InputProps={{ readOnly: true }}
                                    sx={{ backgroundColor: getBgColor('Description') }}
                                />
                            </Grid>
                            <Grid size={6}>
                                <TextField
                                    label="Medicare Rate"
                                    fullWidth
                                    value={`$${code.MedicareRate || 0}`}
                                    InputProps={{ readOnly: true }}
                                    sx={{ backgroundColor: getBgColor('MedicareRate') }}
                                />
                            </Grid>
                            <Grid size={6}>
                                <TextField
                                    label="Medicaid Rate"
                                    fullWidth
                                    value={`$${code.MedicaidRate || 0}`}
                                    InputProps={{ readOnly: true }}
                                    sx={{ backgroundColor: getBgColor('MedicaidRate') }}
                                />
                            </Grid>
                            <Grid size={12}>
                                <Box sx={{ p: 1, border: '1px solid #ccc', borderRadius: 1, backgroundColor: getBgColor('HasMultiUnit') }}>
                                    <Typography variant="caption" color="textSecondary" display="block">
                                        Has Multi-Unit
                                    </Typography>
                                    <Typography>{code.HasMultiUnit ? 'Yes' : 'No'}</Typography>
                                </Box>
                            </Grid>
                            <Grid size={12}>
                                <Box sx={{ p: 1, border: '1px solid #ccc', borderRadius: 1, backgroundColor: getBgColor('PlaceOfEncounterIds') }}>
                                    <Typography variant="caption" color="textSecondary" display="block">
                                        Places of Encounter
                                    </Typography>
                                    <Typography>{getPlacesNames(code.PlaceOfEncounterIds)}</Typography>
                                </Box>
                            </Grid>
                            <Grid size={12}>
                                <Box sx={{ p: 1, border: '1px solid #ccc', borderRadius: 1, backgroundColor: getBgColor('AllowedModifierCodes') }}>
                                    <Typography variant="caption" color="textSecondary" display="block">
                                        Modifiers
                                    </Typography>
                                    <Typography>{getModifierNames(code.AllowedModifierCodes)}</Typography>
                                </Box>
                            </Grid>
                        </>
                    )}
                </Grid>
            </Paper>
        );
    };

    const columns: GridColDef[] = [
        { field: 'RequestedDate', headerName: 'Requested Date', flex: 1, valueGetter: (params, row) => new Date(row.RequestedDate).toLocaleDateString() },
        {
            field: 'RequestedBy',
            headerName: 'Requested By',
            minWidth: 120,
            flex: 1,
            valueGetter: (value, row) => row.RequestedBy?.FullName || 'Unknown',
        },
        {
            field: 'action',
            headerName: 'Action',
            flex: 1,
            renderCell: (params) => (
                <Button variant="contained" size="small" onClick={() => handleOpenDiff(params.row)}>
                    Review
                </Button>
            ),
        },
    ];

    return (
        <Box sx={{ height: 600, width: '100%', mt: 2 }}>
            <DataGrid rows={pendingCPTCodeChanges} columns={columns} getRowId={(row) => row.Id} />

            <Dialog open={showDiffModal} onClose={() => setShowDiffModal(false)} maxWidth="lg" fullWidth>
                <DialogTitle>Review CPT Code Change</DialogTitle>
                <DialogContent>
                    <Box display="flex" gap={2} mt={1}>
                        {renderCodeDetails(selectedChange?.OriginalCode, 'Original (Current)', selectedChange?.ProposedCode)}
                        {renderCodeDetails(selectedChange?.ProposedCode, 'Proposed Changes', selectedChange?.OriginalCode)}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button variant="outlined" sx={{ minWidth: '200px' }} onClick={() => setShowDiffModal(false)}>
                        Cancel
                    </Button>
                    {hasApprovePermission && (
                        <>
                            <Button variant="outlined" sx={{ minWidth: '200px' }} color="error" onClick={() => handleApprove(false)}>
                                Deny
                            </Button>
                            <Button variant="contained" sx={{ minWidth: '200px' }} color="success" onClick={() => handleApprove(true)}>
                                Approve
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default PendingCPTCodeChanges;
